import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'

// Registering only has an effect in the browser; on the server the import is inert.
if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger, useGSAP)
    ScrollTrigger.config({ ignoreMobileResize: true })
}

export { gsap, ScrollTrigger, useGSAP }

// Breakpoint conditions shared by every section's gsap.matchMedia() setup.
export const MOTION_QUERIES = {
    desktop: '(min-width: 1024px) and (prefers-reduced-motion: no-preference)',
    mobile: '(max-width: 1023.98px) and (prefers-reduced-motion: no-preference)',
    reduced: '(prefers-reduced-motion: reduce)'
}

export const prefersReducedMotion = (): boolean => window.matchMedia(MOTION_QUERIES.reduced).matches

// A scroll-scrubbed timeline with invalidateOnRefresh is reverted and re-rendered only up to its
// current position whenever ScrollTrigger refreshes (fonts loading, resizing). Steps further along
// then lose their hidden starting styles and show too early on the first pass down. Pass this as
// the trigger's onRefresh: it renders to the end and back so every step records its start again.
export const primeOnRefresh = (self: ScrollTrigger): void => {
    const timeline = self.animation

    if (!timeline) return

    const current = timeline.progress()

    timeline.progress(1, true).progress(current, true)
}

// One fromTo per target, spaced by `each`, instead of a single tween with `stagger`. A staggered
// tween keeps its steps in a hidden inner timeline, and when ScrollTrigger refreshes a timeline
// with invalidateOnRefresh it only re-applies the starting state of the first target of that inner
// timeline: every later letter or line then shows before its turn, which is what made the chapter
// texts overlap on the first scroll. Separate tweens each get their starting state back.
// Use this instead of `stagger` inside any scroll timeline that has invalidateOnRefresh.
export const fromToEach = (
    timeline: gsap.core.Timeline,
    targets: Array<Element>,
    fromVars: gsap.TweenVars,
    toVars: gsap.TweenVars,
    position: number,
    each: number
): gsap.core.Timeline => {
    targets.forEach((target, index) => {
        timeline.fromTo(target, { ...fromVars }, { ...toVars }, position + index * each)
    })

    return timeline
}

export const isDesktopViewport = (): boolean => window.matchMedia('(min-width: 1024px)').matches

// Deterministic pseudo random numbers, identical on the server and in the browser.
export const createRandom = (seed: number): (() => number) => {
    let state = seed >>> 0

    return () => {
        state = (state + 0x6d2b79f5) >>> 0

        let t = state

        t = Math.imul(t ^ (t >>> 15), t | 1)
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61)

        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}
